'use client';

import { useCallback, useState } from 'react';
import posthog from 'posthog-js';
import { PAID_REPORTS, type PaidReportAssessmentId } from '@timeblock/assessment-content/paid-reports';
import { captureCheckoutError } from '@/lib/posthog-tracking';
import { getStoredAttribution } from '@/lib/utm';
import { metaPixelEvents, generateEventId } from '@/lib/metaPixel';

interface UseReportCheckoutOptions {
  runId: string | null;
  assessmentType: PaidReportAssessmentId;
  customerEmail: string | null;
  // Where the buyer clicked (cta_location) and which offer layout they saw.
  trackingProperties?: Record<string, string>;
}

/**
 * Opens the paid report checkout (Stripe embedded) for one run.
 * Shared by the report-offer pages and the result pages' inline offers, so all of
 * them create the same session and fire the same events.
 */
export function useReportCheckout({ runId, assessmentType, customerEmail, trackingProperties }: UseReportCheckoutOptions) {
  const report = PAID_REPORTS[assessmentType];
  const [isLoading, setIsLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createCheckoutSession = useCallback(async () => {
    if (!runId) {
      setError('We could not find your assessment. Please open this page from your results.');
      return null;
    }

    setIsLoading(true);
    setError(null);
    const checkoutStartedAt = Date.now();
    const eventProperties = {
      run_id: runId,
      assessment_type: assessmentType,
      minitools_assessment_type: assessmentType,
      product: report.product,
      price_usd: report.priceUsd,
      ...trackingProperties,
    };

    posthog.capture('minitools_report_checkout_initiate', eventProperties);
    metaPixelEvents.trackInitiateCheckout(assessmentType, report.priceUsd, generateEventId());

    try {
      const response = await fetch('/tools/api/checkout/create-report-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          runId,
          assessment_type: assessmentType,
          customer_email: customerEmail,
          attribution: getStoredAttribution(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Checkout failed: ${response.status}`);
      }

      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }
      // The session is created with ui_mode 'embedded', so a clientSecret is the only
      // shape the route returns.
      if (!data.clientSecret) {
        throw new Error('Invalid response from checkout service');
      }

      posthog.capture('minitools_report_checkout_ready', {
        ...eventProperties,
        duration_ms: Date.now() - checkoutStartedAt,
      });
      return data.clientSecret as string;
    } catch (checkoutError) {
      const message = checkoutError instanceof Error ? checkoutError.message : 'An unexpected error occurred';
      captureCheckoutError(checkoutError, 'session_creation', {
        assessmentType,
        additionalData: { product: report.product, run_id: runId },
      });
      setError(`Unable to open checkout. ${message}. Please try again or email support@lifehack.org.`);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [assessmentType, customerEmail, report, runId, trackingProperties]);

  const startCheckout = useCallback(async () => {
    const nextClientSecret = await createCheckoutSession();
    if (nextClientSecret) setClientSecret(nextClientSecret);
    return nextClientSecret;
  }, [createCheckoutSession]);

  const refreshCheckout = useCallback(async () => {
    const nextClientSecret = await createCheckoutSession();
    if (!nextClientSecret) {
      throw new Error('Unable to create a fresh checkout session');
    }
    return nextClientSecret;
  }, [createCheckoutSession]);

  const closeCheckout = useCallback(() => setClientSecret(''), []);

  return {
    startCheckout,
    refreshCheckout,
    closeCheckout,
    clientSecret,
    isLoading,
    error,
    priceUsd: report.priceUsd,
  };
}
